import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import ProgressBar from "@galaxy-io/dls/feedback/ProgressBar";
import Icon, { IconVariant } from "@galaxy-io/dls/icons/Icon";
import Pagination, { PaginationSize } from "@galaxy-io/dls/navigation/Pagination";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import type {
  ImportDetail,
  ImportFile,
  ImportPlan,
  ImportResolution,
  ImportRow,
  ImportSettings,
} from "@gtm/contracts/imports";
import type { CsvImportRun } from "@gtm/contracts/workflows/csv-import";
import {
  BuildingsIcon,
  ChatTextIcon,
  UserIcon,
  UserPlusIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { type ComponentProps, useState } from "react";
import { Button } from "@/components/ui/button";
import { Inline } from "@/components/workspace/people-styles";
import { SelectField } from "@/components/workspace/select-field";
import { Feedback, Status } from "@/components/workspace/status";
import { count } from "./import-fields";
import {
  Issue,
  List,
  MappingRow,
  Matches,
  Outcomes,
  Pager,
  ReviewLine,
  ReviewRow,
  RowDetail,
  Section,
  SectionHeading,
  Stacked,
} from "./import-layout";

export const PAGE_SIZE = 40;
export type RowState = "ready" | "review" | "skip" | "imported" | "skipped";
export function rowState(row: ImportRow): RowState {
  if (row.status === "imported") return "imported";
  if (row.status === "skipped") return "skipped";
  if (row.resolution.skip) return "skip";
  return row.plan?.errors.length ? "review" : "ready";
}
const stateLabels: Record<RowState, string> = {
  ready: "Ready",
  review: "Needs review",
  skip: "Will skip",
  imported: "Imported",
  skipped: "Skipped",
};
function RowStatus({ state }: { state: RowState }) {
  const value =
    state === "ready"
      ? "active"
      : state === "review"
        ? "pending"
        : state === "imported"
          ? "completed"
          : "skipped";
  return <Status value={value} label={stateLabels[state]} />;
}
// Choices the match pickers already present; repeating them as issues is noise.
const choiceIssues = ["Review possible person matches.", "Review possible company matches."];

type Outcome = { icon: ComponentProps<typeof Icon>["component"]; label: string };
function outcomes(plan: ImportPlan, resolution: ImportResolution): Outcome[] {
  const list: Outcome[] = [];
  if (plan.signalAction === "Create signal") list.push({ icon: ChatTextIcon, label: "New signal" });
  if (plan.signalAction === "Already imported")
    list.push({ icon: ChatTextIcon, label: "Already imported" });
  const undecided = (candidates: unknown[], create?: boolean, id?: string) =>
    candidates.length && !create && !id;
  if (
    plan.personAction !== "None" &&
    !undecided(plan.personCandidates, resolution.createPerson, resolution.personId)
  )
    list.push(
      plan.personAction === "Create person"
        ? { icon: UserPlusIcon, label: "New person" }
        : plan.personAction === "Link message author"
          ? { icon: UserIcon, label: "Existing author" }
          : { icon: UserIcon, label: plan.personAction.replace(/^Link person: /, "") },
    );
  if (
    plan.companyAction !== "None" &&
    !undecided(plan.companyCandidates, resolution.createCompany, resolution.companyId)
  )
    list.push(
      plan.companyAction === "Create company"
        ? { icon: BuildingsIcon, label: "New company" }
        : { icon: BuildingsIcon, label: plan.companyAction.replace(/^Link company: /, "") },
    );
  return list;
}

function rowSummary(row: ImportRow, file: ImportFile, settings: ImportSettings) {
  const value = (field: keyof ImportSettings["mapping"]) => {
    const header = settings.mapping[field];
    return header ? row.data[header]?.trim() || "" : "";
  };
  if (file.kind === "companies")
    return { title: value("company_name") || `Row ${row.number}`, detail: value("company_domain") };
  const title = value("author_name") || value("email") || `Row ${row.number}`;
  return file.kind === "signals"
    ? { title, detail: value("message_text") }
    : {
        title,
        detail: [value("email"), value("company_name")].filter(Boolean).join(" · "),
      };
}

type Filter = "all" | RowState;
export function ReviewStep({
  file,
  settings,
  detail,
  execution,
  page,
  locked,
  sourceOptions,
  onPage,
  onResolve,
  onSource,
}: {
  file: ImportFile;
  settings: ImportSettings;
  detail: ImportDetail | undefined;
  execution: CsvImportRun | null | undefined;
  page: number;
  locked: boolean;
  sourceOptions: { value: string; label: string }[];
  onPage: (page: number) => void;
  onResolve: (row: number, resolution: ImportResolution) => void;
  onSource: (value: string, sourceId: string) => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const rows = detail?.rows ?? [];
  const states = rows.map(rowState);
  const tally = (s: RowState) => states.filter((x) => x === s).length;
  const tabs: [Filter, string, number][] = [
    ["all", "All", rows.length],
    ["review", "Needs review", tally("review")],
    ["ready", "Ready", tally("ready")],
    ["skip", "Will skip", tally("skip")],
    ["imported", "Imported", tally("imported")],
    ["skipped", "Skipped", tally("skipped")],
  ];
  // A filter whose rows have all moved on (imported, resolved) falls back to all.
  const active = tabs.some(([key, , n]) => key === filter && n > 0) ? filter : "all";
  const visible = rows.filter((_, i) => active === "all" || states[i] === active);
  const running = execution?.status === "RUNNING";
  const completed = detail?.completed ?? file.completed;
  const pages = Math.ceil(file.total / PAGE_SIZE);
  return (
    <>
      {running ? (
        <Section aria-label="Import progress" role="status">
          <SectionHeading>
            <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
              Importing… {execution.scanned.toLocaleString()} of {count(file.total, "row")} checked
            </Text>
            <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
              You can close this window
            </Text>
          </SectionHeading>
          <ProgressBar
            ariaLabel="Import progress"
            value={file.total ? (execution.scanned / file.total) * 100 : 0}
          />
        </Section>
      ) : execution?.status === "COMPLETED" ? (
        <Feedback
          message={
            completed >= file.total
              ? `Import finished. All ${count(file.total, "row")} are done.`
              : `Import finished. ${completed.toLocaleString()} of ${count(file.total, "row")} done; the rest need review.`
          }
        />
      ) : execution && execution.status !== "RUNNING" ? (
        <Feedback
          error
          message={execution.error || `Import ${execution.status.toLowerCase()}. Try again.`}
        />
      ) : null}
      {!!detail?.sourceValues.length && (
        <Section aria-label="Source names">
          <SectionHeading>
            <div>
              <Text weight={TextWeight.MEDIUM}>Source names</Text>
              <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                Match each source name in your file to a Galaxy source.
              </Text>
            </div>
          </SectionHeading>
          <List>
            {detail.sourceValues.map((value) => (
              <MappingRow key={value}>
                <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM} lineClamp={1}>
                  {value}
                </Text>
                <SelectField
                  label={`Source for ${value}`}
                  compact
                  width="100%"
                  search
                  placeholder={settings.sourceId ? "Use default source" : "Choose a source"}
                  disabled={locked || (!!completed && !!detail.settings.sources[value])}
                  value={settings.sources[value] || ""}
                  options={[
                    { value: "", label: settings.sourceId ? "Use default source" : "Not set" },
                    ...sourceOptions,
                  ]}
                  onChange={(v) => onSource(value, v)}
                />
                <div />
              </MappingRow>
            ))}
          </List>
        </Section>
      )}
      <Section aria-label="Rows">
        <SectionHeading>
          <div>
            <Text weight={TextWeight.MEDIUM}>
              {pages > 1
                ? `Rows ${((page - 1) * PAGE_SIZE + 1).toLocaleString()}–${Math.min(page * PAGE_SIZE, file.total).toLocaleString()} of ${file.total.toLocaleString()}`
                : count(file.total, "row")}
            </Text>
            <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
              Ready rows import now. Rows that need review wait until you resolve them.
            </Text>
          </div>
        </SectionHeading>
        <Tabs
          ariaLabel="Filter rows"
          value={active}
          onChange={(value) => setFilter(value as typeof active)}
          items={tabs
            .filter(([key, , n]) => key === "all" || n > 0)
            .map(([id, label, count]) => ({ id, label, count }))}
        />
        {!detail ? (
          <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
            Checking rows…
          </Text>
        ) : !visible.length ? (
          <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
            No rows here{pages > 1 ? " on this page" : ""}.
          </Text>
        ) : (
          <List>
            {visible.map((row) => (
              <ImportRowItem
                key={row.number}
                row={row}
                file={file}
                settings={settings}
                locked={locked}
                onResolve={onResolve}
              />
            ))}
          </List>
        )}
        {pages > 1 && (
          <Pager>
            <Pagination
              ariaLabel="Import rows pagination"
              total={file.total}
              pageSize={PAGE_SIZE}
              value={page}
              onChange={onPage}
              size={PaginationSize.SMALL}
              isCompact
              fillWidth
            />
          </Pager>
        )}
      </Section>
    </>
  );
}

function ImportRowItem({
  row,
  file,
  settings,
  locked,
  onResolve,
}: {
  row: ImportRow;
  file: ImportFile;
  settings: ImportSettings;
  locked: boolean;
  onResolve: (row: number, resolution: ImportResolution) => void;
}) {
  const state = rowState(row);
  const { title, detail } = rowSummary(row, file, settings);
  const plan = row.plan;
  const issues = plan?.errors.filter((e) => !choiceIssues.includes(e)) ?? [];
  const r = row.resolution;
  const open = state !== "skip" && !!plan;
  // A choice made here hides its picker, so offer a way back to it.
  const chosen = !!(r.personId || r.createPerson || r.companyId || r.createCompany);
  return (
    <ReviewRow aria-label={`Row ${row.number}`}>
      <ReviewLine>
        <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY} family={FontFamily.MONO}>
          {row.number}
        </Text>
        <Stacked>
          <Text
            size={TextSize.BODY_SM}
            weight={TextWeight.MEDIUM}
            variant={state === "skip" || state === "skipped" ? TextVariant.TERTIARY : undefined}
            lineClamp={1}
          >
            {title}
          </Text>
          {detail && (
            <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY} lineClamp={1}>
              {detail}
            </Text>
          )}
        </Stacked>
        <Outcomes>
          {open &&
            outcomes(plan, r).map((o) => (
              <div key={o.label}>
                <Icon component={o.icon} size={14} variant={IconVariant.TERTIARY} />
                <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY} lineClamp={1}>
                  {o.label}
                </Text>
              </div>
            ))}
          <RowStatus state={state} />
        </Outcomes>
        {state === "imported" || state === "skipped" ? (
          <div />
        ) : (
          <Button
            label={r.skip ? "Include" : "Skip"}
            variant={ButtonVariant.TERTIARY}
            size={ButtonSize.SMALL}
            isDisabled={locked}
            onClick={() => onResolve(row.number, { ...r, skip: !r.skip })}
          />
        )}
      </ReviewLine>
      {open &&
        (!!issues.length ||
          !!plan.personCandidates.length ||
          !!plan.companyCandidates.length ||
          chosen) && (
          <RowDetail>
            {!!issues.length && (
              <div role="list" aria-label="Issues">
                {issues.map((e) => (
                  <Issue role="listitem" key={e}>
                    <Icon component={WarningCircleIcon} size={14} variant={IconVariant.WARNING} />
                    <Text size={TextSize.BODY_SM} variant={TextVariant.WARNING}>
                      {e}
                    </Text>
                  </Issue>
                ))}
              </div>
            )}
            {(!!plan.personCandidates.length || !!plan.companyCandidates.length) && (
              <Matches>
                {!!plan.personCandidates.length && (
                  <SelectField
                    label={`Possible ${plan.personCandidates.length === 1 ? "match" : "matches"} for this person`}
                    placeholder="Choose a person…"
                    disabled={locked}
                    value={r.personId || (r.createPerson ? "new" : "")}
                    options={[
                      ...plan.personCandidates.map((p) => ({
                        value: p.id,
                        label: p.detail ? `${p.name} · ${p.detail}` : p.name,
                      })),
                      { value: "new", label: "None of these — create a new person" },
                    ]}
                    onChange={(v) =>
                      onResolve(row.number, {
                        ...r,
                        personId: v && v !== "new" ? v : undefined,
                        createPerson: v === "new",
                      })
                    }
                  />
                )}
                {!!plan.companyCandidates.length && (
                  <SelectField
                    label={`Possible ${plan.companyCandidates.length === 1 ? "match" : "matches"} for this company`}
                    placeholder="Choose a company…"
                    disabled={locked}
                    value={r.companyId || (r.createCompany ? "new" : "")}
                    options={[
                      ...plan.companyCandidates.map((c) => ({
                        value: c.id,
                        label: c.detail ? `${c.name} · ${c.detail}` : c.name,
                      })),
                      { value: "new", label: "None of these — create a new company" },
                    ]}
                    onChange={(v) =>
                      onResolve(row.number, {
                        ...r,
                        companyId: v && v !== "new" ? v : undefined,
                        createCompany: v === "new",
                      })
                    }
                  />
                )}
              </Matches>
            )}
            {chosen && (
              <Inline>
                {(r.personId || r.createPerson) && (
                  <Button
                    label="Change person match"
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.SMALL}
                    isDisabled={locked}
                    onClick={() =>
                      onResolve(row.number, { ...r, personId: undefined, createPerson: false })
                    }
                  />
                )}
                {(r.companyId || r.createCompany) && (
                  <Button
                    label="Change company match"
                    variant={ButtonVariant.TERTIARY}
                    size={ButtonSize.SMALL}
                    isDisabled={locked}
                    onClick={() =>
                      onResolve(row.number, { ...r, companyId: undefined, createCompany: false })
                    }
                  />
                )}
              </Inline>
            )}
          </RowDetail>
        )}
    </ReviewRow>
  );
}
